import { format } from "date-fns";

/**
 * Parse an entry date string as a local calendar date.
 *
 * Entry rows currently come from both legacy (`MM/dd/yyyy`) and v2
 * (`yyyy-MM-dd`) surfaces. Do not use `new Date("yyyy-MM-dd")` for date-only
 * strings: JavaScript treats that form as UTC midnight, which shifts to the
 * previous day in negative-offset timezones.
 */
export function parseEntryDate(dateStr: string): Date {
	const trimmed = dateStr.trim();

	const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
	if (slashMatch) {
		const [, mm, dd, yyyy] = slashMatch.map(Number);
		return new Date(yyyy, mm - 1, dd);
	}

	const dashMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
	if (dashMatch) {
		const [, yyyy, mm, dd] = dashMatch.map(Number);
		return new Date(yyyy, mm - 1, dd);
	}

	return new Date(trimmed);
}

export interface DateRange {
	from: Date;
	to: Date;
}

export interface DateRangeBoundary {
	from?: Date;
	to?: Date;
}

/** Format a Date for v1/v2 entry payloads that still expect "MM/dd/yyyy". */
export function formatDateForApi(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const year = date.getFullYear();
	return `${month}/${day}/${year}`;
}

/** Stable local calendar key suitable for memo/selection scopes. */
export function localDateKey(date: Date | undefined): string {
	if (!date) return "";
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${date.getFullYear()}-${month}-${day}`;
}

export function isSameLocalDate(left: Date, right: Date): boolean {
	return (
		left.getFullYear() === right.getFullYear() &&
		left.getMonth() === right.getMonth() &&
		left.getDate() === right.getDate()
	);
}

export function isCompleteDateRange(
	range: DateRangeBoundary | null | undefined,
): range is DateRange {
	return !!(range?.from && range.to);
}

export function formatDateRange(range: DateRange): string {
	if (isSameLocalDate(range.from, range.to)) return format(range.from, "MMM d");
	const currentYear = new Date().getFullYear();
	const fromYear = range.from.getFullYear();
	const toYear = range.to.getFullYear();
	if (fromYear !== toYear) {
		return `${format(range.from, "MMM d, yyyy")} – ${format(range.to, "MMM d, yyyy")}`;
	}
	if (fromYear !== currentYear) {
		return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d, yyyy")}`;
	}
	return `${format(range.from, "MMM d")} – ${format(range.to, "MMM d")}`;
}

export function startOfLocalDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function endOfLocalDay(date: Date): Date {
	return new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate(),
		23,
		59,
		59,
		999,
	);
}

export function isWithinLocalDateRange(
	date: Date,
	range: DateRangeBoundary,
): boolean {
	if (!range.from || !range.to) return false;
	return date >= startOfLocalDay(range.from) && date <= endOfLocalDay(range.to);
}
